import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Building2, MapPin, Network, Route as RouteIcon } from "lucide-react";
import NotFound from "@/pages/NotFound";
import { getCityHub } from "@/data/content/cityHubs";
import { ROUTE_PATHS } from "@/routes/routePaths";

export default function CityHubPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/\/$/, "").slice(1);
  const hub = getCityHub(slug);

  if (!hub) return <NotFound />;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-muted/30">
        <div className="container py-10 sm:py-14">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{hub.eyebrow}</p>
          <h1 className="mt-2 max-w-3xl text-3xl font-bold text-foreground md:text-4xl">{hub.title}</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">{hub.overview}</p>
        </div>
      </header>

      {hub.dailyCommute ? (
        <section className="container py-10 sm:py-12">
          <div className="mx-auto max-w-4xl rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold text-foreground md:text-2xl">{hub.dailyCommute.title}</h2>
            </div>
            <p className="mt-3 text-muted-foreground">{hub.dailyCommute.description}</p>
            <p className="mt-4 text-sm">
              <strong className="text-foreground">Peak windows:</strong>{" "}
              <span className="text-muted-foreground">{hub.dailyCommute.peakTimes}</span>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{hub.dailyCommute.savingsExample}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {hub.dailyCommute.zones.map((z) => (
                <span key={z} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {z}
                </span>
              ))}
            </div>
            <Link
              to={hub.dailyCommute.commutePath}
              className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              {hub.city} daily commute guide <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      ) : null}

      <section className="bg-muted/40 py-10 sm:py-12">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center gap-2">
              <RouteIcon className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold text-foreground md:text-2xl">Popular routes from {hub.city}</h2>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {hub.popularRoutes.map((route) => (
                <Link
                  key={route.path}
                  to={route.path}
                  className="group rounded-2xl border border-border bg-card p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-foreground group-hover:text-primary">{route.label}</h3>
                    {route.distance ? (
                      <span className="shrink-0 text-xs text-muted-foreground">{route.distance}</span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{route.description}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                    View route <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container py-10 sm:py-12 lg:pb-14">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground md:text-2xl">Why Duri in {hub.city}</h2>
          </div>
          <ul className="mt-6 space-y-4">
            {hub.localReasons.map((reason) => (
              <li key={reason} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>{reason}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={ROUTE_PATHS.FIND_RIDES}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
            >
              Find rides <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-3 text-sm font-semibold text-foreground hover:bg-muted/50"
            >
              Intercity hub
            </Link>
          </div>

          {hub.relatedHubs.length > 0 ? (
            <div className="mt-10 rounded-2xl border border-border bg-muted/30 p-5">
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-foreground">Related content hubs</h3>
              </div>
              <ul className="mt-3 space-y-2">
                {hub.relatedHubs.map((related) => (
                  <li key={related.path}>
                    <Link to={related.path} className="text-sm font-medium text-primary hover:underline">
                      {related.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
